import React, { useState, useEffect } from 'react';
import {
    Paper, Typography, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Grid, TextField, MenuItem, Select, FormControl,
    InputLabel, TablePagination, makeStyles, Box,
} from '@material-ui/core';
import LoaderWithBackDrop from '../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import { getDqtLedgerList } from '../../../../services/billing.service';

/**
 * DQTokenLedgerTab — every DQ Token movement across all workspaces.
 *
 * The DQT counterpart of the credits ledger: each row is one immutable entry —
 * a grant, a checkout purchase, or a conversion into credits or licences. Grants
 * and purchases also carry the money and the rate behind them, so a balance can
 * always be traced back to what was paid.
 *
 * Read-only. DQT is moved by the grant action, checkout, and the partner's own
 * conversions — never from this screen.
 */

const TRANSACTION_TYPES = {
    grant:      'Grant (offline payment)',
    purchase:   'Purchase (checkout)',
    conversion: 'Conversion',
    assignment: 'Assignment (to a client)',
    build:      "Build (partner's own)",
    adjustment: 'Adjustment',
};

const TYPE_COLOR = {
    grant:      'primary',
    purchase:   'primary',
    conversion: 'default',
    assignment: 'default',
    build:      'default',
    adjustment: 'secondary',
};

const useStyles = makeStyles((theme) => ({
    paper: { padding: theme.spacing(3), marginBottom: theme.spacing(3) },
    table: { minWidth: 650 },
    filterContainer: { marginBottom: theme.spacing(3) },
    tableHeaderCell: { fontWeight: 600, backgroundColor: theme.palette.background.default },
    amountPositive: { color: theme.palette.success.main, fontWeight: 'bold' },
    amountNegative: { color: theme.palette.error.main, fontWeight: 'bold' },
}));

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

/** What a row did, in words — the ledger's own fields say it precisely. */
const describeEntry = (e) => {
    if (e.transaction_type === 'grant') {
        return `Granted against payment ${e.payment_reference || '—'}`;
    }
    if (e.transaction_type === 'conversion') {
        const unit = e.conversion?.unit === 'licence' ? 'licences' : 'credits';
        return `Converted into ${fmt(e.conversion?.quantity)} ${unit}`;
    }
    return e.description || '—';
};

const DQTokenLedgerTab = () => {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        transaction_type: '',
        dq_insider_id: '',
        start_date: '',
        end_date: '',
        page: 1,
        limit: 20,
    });
    // Typing in the workspace box filters on blur / Enter, not on every keystroke.
    const [insiderInput, setInsiderInput] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const { transaction_type, dq_insider_id, start_date, end_date, page, limit } = filters;
                const result = await getDqtLedgerList({
                    page,
                    limit,
                    transaction_type: transaction_type || undefined,
                    dq_insider_id: dq_insider_id || undefined,
                    start_date: start_date || undefined,
                    end_date: end_date || undefined,
                });
                if (result && result.data) {
                    setEntries(result.data.entries || []);
                    setTotal(result.data.total || 0);
                }
            } catch (error) {
                // invokeApi has already shown the error.
                console.error('Error fetching DQT ledger:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [filters]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1,
        }));
    };

    return (
        <div>
            <Paper className={classes.paper} elevation={1}>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginBottom: 4 }}>
                    DQ Token Ledger
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                    Every DQ Token movement across all workspaces — grants, checkout purchases and
                    conversions into credits or licences. Entries are immutable; corrections are
                    made with an adjustment.
                </Typography>

                <Grid container spacing={2} className={classes.filterContainer}>
                    <Grid item xs={12} sm={3}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Transaction Type</InputLabel>
                            <Select
                                value={filters.transaction_type}
                                onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                                label="Transaction Type"
                            >
                                <MenuItem value=""><em>All Transactions</em></MenuItem>
                                {Object.keys(TRANSACTION_TYPES).map((key) => (
                                    <MenuItem key={key} value={key}>{TRANSACTION_TYPES[key]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Workspace (dq_insider_id)"
                            value={insiderInput}
                            onChange={(e) => setInsiderInput(e.target.value)}
                            onBlur={() => handleFilterChange('dq_insider_id', insiderInput.trim())}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleFilterChange('dq_insider_id', insiderInput.trim()); }}
                            variant="outlined" size="small" fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="From" type="date" value={filters.start_date}
                            onChange={(e) => handleFilterChange('start_date', e.target.value)}
                            InputLabelProps={{ shrink: true }} variant="outlined" size="small" fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="To" type="date" value={filters.end_date}
                            onChange={(e) => handleFilterChange('end_date', e.target.value)}
                            InputLabelProps={{ shrink: true }} variant="outlined" size="small" fullWidth
                        />
                    </Grid>
                </Grid>

                <TableContainer component={Paper} elevation={0}>
                    <Table className={classes.table} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeaderCell}>Workspace</TableCell>
                                <TableCell className={classes.tableHeaderCell}>Date</TableCell>
                                <TableCell className={classes.tableHeaderCell}>Type</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">DQ Tokens</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Balance After</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Money (USD)</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Rate</TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ minWidth: 200 }}>Detail</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {entries.map((entry) => (
                                <TableRow key={entry._id}>
                                    <TableCell>
                                        <Typography variant="caption" style={{ fontWeight: 600 }}>
                                            {entry.dq_insider_id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
                                            {new Date(entry.createdAt).toLocaleString('en-GB', {
                                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                                            })}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={TRANSACTION_TYPES[entry.transaction_type] || entry.transaction_type}
                                            size="small"
                                            color={TYPE_COLOR[entry.transaction_type] || 'default'}
                                            variant={entry.transaction_type === 'conversion' ? 'outlined' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <span className={entry.amount_dqt >= 0 ? classes.amountPositive : classes.amountNegative}>
                                            {entry.amount_dqt >= 0 ? '+' : '−'}{fmt(Math.abs(entry.amount_dqt))}
                                        </span>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>{fmt(entry.balance_after)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {typeof entry.amount_usd === 'number'
                                            ? <Typography variant="body2">US${fmt(entry.amount_usd)}</Typography>
                                            : <Typography variant="body2" color="textSecondary">—</Typography>}
                                    </TableCell>
                                    <TableCell align="right">
                                        {typeof entry.usd_per_dqt === 'number'
                                            ? <Typography variant="body2" color="textSecondary">US${entry.usd_per_dqt}/DQT</Typography>
                                            : <Typography variant="body2" color="textSecondary">—</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Box component="div" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            <Typography variant="body2">{describeEntry(entry)}</Typography>
                                            {entry.created_by?.label ? (
                                                <Typography variant="caption" color="textSecondary">by {entry.created_by.label}</Typography>
                                            ) : null}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {entries.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" style={{ padding: 40 }}>
                                        <Typography color="textSecondary">No DQ Token entries found.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[20, 50, 100, 200]}
                    component="div"
                    count={total}
                    rowsPerPage={filters.limit}
                    page={filters.page - 1}
                    onPageChange={(e, newPage) => handleFilterChange('page', newPage + 1)}
                    onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                />

                <LoaderWithBackDrop loading={loading} />
            </Paper>
        </div>
    );
};

export default DQTokenLedgerTab;
